// @flow

import * as React from 'react';
import Card from 'sail/Card';
import Box from 'sail/Box';
import {Text} from 'sail/Text';
import {UnstyledLink} from 'sail/UnstyledLink';
import classNames from 'classnames';
import {CodeBlock} from 'docs/components';
import {usePrefs} from 'docs/data';
import './MarkdocExample.scss';

type MarkdocExampleProps = {|
  exampleCode: string,
  hidePreview: boolean,
  showPreviewFirst: boolean,
  children: React.Node,
|};

const SHOW_PREVIEW_PREF = 'markdoc-show-preview';

export const MarkdocExample = ({
  exampleCode,
  hidePreview = false,
  showPreviewFirst = false,
  children,
}: MarkdocExampleProps) => {
  const {getPref, setPref} = usePrefs();
  const showPreviewPref = getPref(SHOW_PREVIEW_PREF);
  const showPreview =
    (showPreviewPref === null ? true : showPreviewPref) && !hidePreview;

  const toggleShowPreview = () => {
    setPref(SHOW_PREVIEW_PREF, !showPreview);
  };

  const codeBlock = (
    <CodeBlock filename="Source Code" language="markdoc">
      {`${exampleCode}\n`}
    </CodeBlock>
  );

  const preview = (
    <Box>
      <Card radius={showPreviewFirst ? 'none' : 'bottom'} shadow="keyline">
        <Box padding={{all: 20}}>{children}</Box>
      </Card>
    </Box>
  );

  const dividerBottomRadius = showPreview || showPreviewFirst ? 0 : 4;
  const dividerTopRadius = showPreviewFirst ? 4 : 0;

  const previewContainer = (
    <Box className="MarkdocExample-Preview">
      <Box
        padding={{vertical: 4, horizontal: 12}}
        background="gray50"
        divider={{color: 'light', width: 1, side: 'all'}}
        style={{
          margin: '0 -1px',
          borderBottomLeftRadius: dividerBottomRadius,
          borderBottomRightRadius: dividerBottomRadius,
          borderTopLeftRadius: dividerTopRadius,
          borderTopRightRadius: dividerTopRadius,
        }}
      >
        {!hidePreview && (
          <UnstyledLink href="#" onClick={() => toggleShowPreview()}>
            <Text color="gray" weight="medium">
              {showPreview ? 'Hide Preview' : 'Show Preview'}
            </Text>
          </UnstyledLink>
        )}
      </Box>
      {showPreview && preview}
    </Box>
  );

  return (
    <Box
      className={classNames(
        'MarkdocExample',
        showPreviewFirst && 'MarkdocExample--previewFirst',
      )}
      margin={{top: 24, bottom: 64}}
    >
      {showPreviewFirst && previewContainer}
      <Box className="MarkdocExample-CodeContainer">{codeBlock}</Box>
      {!showPreviewFirst && previewContainer}
    </Box>
  );
};
